/**
 * CollectPaymentModal.tsx
 *
 * Thin wrapper used when "Collect Payment" is launched from a global surface
 * (PaymentsPage, PaymentsDashboardPage) that does not have a patient context
 * yet. Presents a patient search first, then hands off to the unified
 * ChargePaymentModal so every charge in the app uses the same modal.
 */
import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { PatientSearchInput } from '@/components/medical/PatientSearchInput';
import { ChargePaymentModal } from '@/pages/payments/ChargePaymentModal';
import type { PatientSummary } from '@/types/patient.types';

export interface CollectPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CollectPaymentModal({ isOpen, onClose, onSuccess }: CollectPaymentModalProps) {
  const [patient, setPatient] = useState<PatientSummary | null>(null);

  useEffect(() => {
    if (!isOpen) setPatient(null);
  }, [isOpen]);

  if (isOpen && patient?.patientId) {
    return (
      <ChargePaymentModal
        open
        patientId={patient.patientId}
        patientName={patient.displayName ?? undefined}
        onClose={() => {
          setPatient(null);
          onClose();
        }}
        onSaved={() => {
          onSuccess();
          setPatient(null);
        }}
      />
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Collect Payment"
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Select the patient this payment is for. The full charge modal opens next.
        </p>
        <PatientSearchInput
          selectedPatient={patient}
          onSelect={(p) => setPatient(p)}
          placeholder="Search patients…"
        />
      </div>
    </Modal>
  );
}
